import { Box } from '@mui/material';
import { FunctionComponent, useEffect, useRef } from 'react';

import { SvbrgChatMessageStatus, SvbrgChatMessageVariant, SvbrgIntakeStatus } from '@/generated/gql/graphql';
import { ChatMessageProps } from '~/common/types/ChatMessage';
import { IntakeBudgetStep } from '~/routes/IntakeDetail/steps/IntakeBudgetStep/IntakeBudgetStep';
import { IntakeChatRequestDescriptionStep } from '~/routes/IntakeDetail/steps/IntakeChatRequestDescriptionStep/IntakeChatRequestDescriptionStep';
import { IntakeChatRequestMessageStep } from '~/routes/IntakeDetail/steps/IntakeChatRequestMessageStep/IntakeChatRequestMessageStep';
import { IntakeIntentSelectionStep } from '~/routes/IntakeDetail/steps/IntakeIntentSelectionStep/IntakeIntentSelectionStep';
import { IntakeQuoteComplianceStep } from '~/routes/IntakeDetail/steps/IntakeQuoteComplianceStep/IntakeQuoteComplianceStep';
import { IntakeQuoteStep } from '~/routes/IntakeDetail/steps/IntakeQuoteStep/IntakeQuoteStep';
import { IntakeQuoteSupplierConfirmationStep } from '~/routes/IntakeDetail/steps/IntakeQuoteSupplierConfirmationStep/IntakeQuoteSupplierConfirmationStep';
import { IntakeQuoteUploadStep } from '~/routes/IntakeDetail/steps/IntakeQuoteUploadStep/IntakeQuoteUploadStep';
import { IntakeRequestComplexStep } from '~/routes/IntakeDetail/steps/IntakeRequestComplexStep/IntakeRequestComplexStep';
import { IntakeRequestDescriptionStep } from '~/routes/IntakeDetail/steps/IntakeRequestDescriptionStep/IntakeRequestDescriptionStep';
import { IntakeRequestDetailsStep } from '~/routes/IntakeDetail/steps/IntakeRequestDetailsStep/IntakeRequestDetailsStep';
import { IntakeRequestLineItemStep } from '~/routes/IntakeDetail/steps/IntakeRequestLineItemStep/IntakeRequestLineItemStep';
import { IntakeRequestSubmitStep } from '~/routes/IntakeDetail/steps/IntakeRequestSubmitStep/IntakeRequestSubmitStep';
import { IntakeRequestSupplierSelectionStep } from '~/routes/IntakeDetail/steps/IntakeRequestSupplierSelectionStep/IntakeRequestSupplierSelectionStep';
import { IntakeRequirementsUploadStep } from '~/routes/IntakeDetail/steps/IntakeRequirementsUploadStep/IntakeRequirementsUploadStep';
import { IntakeTacticalSourcingSelectionStep } from '~/routes/IntakeDetail/steps/IntakeTacticalSourcingSelectionStep/IntakeTacticalSourcingSelectionStep';

import { useIntakeDetail } from './IntakeDetail.hooks';
import { IntakeAttachmentsStep } from './steps/IntakeAttachmentsStep/IntakeAttachmentsStep';
import { IntakeExtendedLineItemsCheckStep } from './steps/IntakeExtendedLineItemsCheckStep/IntakeExtendedLineItemsCheckStep';
import { IntakeGenerateStatementOfWorkStep } from './steps/IntakeGenerateStatementOfWorkStep/IntakeGenerateStatementOfWorkStep';
import { IntakeNuclearComplianceFormStep } from './steps/IntakeNuclearComplianceFormStep/IntakeNuclearComplianceFormStep';
import { IntakeSupplierSourcingStep } from './steps/IntakeSupplierSourcingStep/IntakeSupplierSourcingStep';

const chatMessageStepMap: {
  [key in SvbrgChatMessageVariant]: FunctionComponent<ChatMessageProps> | undefined;
} = {
  [SvbrgChatMessageVariant.QUOTE_UPLOAD]: IntakeQuoteUploadStep,
  [SvbrgChatMessageVariant.ATTACHMENTS]: IntakeAttachmentsStep,
  [SvbrgChatMessageVariant.REQUEST_DESCRIPTION]: IntakeRequestDescriptionStep,
  [SvbrgChatMessageVariant.REQUEST_INTENT_SELECTION]: IntakeIntentSelectionStep,
  [SvbrgChatMessageVariant.REQUEST_DETAILS]: IntakeRequestDetailsStep,
  [SvbrgChatMessageVariant.CHAT_REQUEST_DESCRIPTION]: IntakeChatRequestDescriptionStep,
  [SvbrgChatMessageVariant.CHAT_REQUEST_MESSAGE]: IntakeChatRequestMessageStep,
  [SvbrgChatMessageVariant.QUOTE]: IntakeQuoteStep,
  [SvbrgChatMessageVariant.QUOTE_COMPLIANCE]: IntakeQuoteComplianceStep,
  [SvbrgChatMessageVariant.REQUEST_BUDGET]: IntakeBudgetStep,
  [SvbrgChatMessageVariant.REQUEST_COMPLEX]: IntakeRequestComplexStep,
  [SvbrgChatMessageVariant.REQUEST_SUBMIT]: IntakeRequestSubmitStep,
  [SvbrgChatMessageVariant.REQUEST_TACTICAL_SOURCING_SELECTION]: IntakeTacticalSourcingSelectionStep,
  [SvbrgChatMessageVariant.QUOTE_SUPPLIER_CONFIRMATION]: IntakeQuoteSupplierConfirmationStep,
  [SvbrgChatMessageVariant.REQUEST_LINE_ITEMS]: IntakeRequestLineItemStep,
  [SvbrgChatMessageVariant.REQUEST_SUPPLIER_SELECTION]: IntakeRequestSupplierSelectionStep,
  [SvbrgChatMessageVariant.REQUIREMENTS_UPLOAD]: IntakeRequirementsUploadStep,
  [SvbrgChatMessageVariant.REQUEST_SOW_GENERATION]: IntakeGenerateStatementOfWorkStep,
  [SvbrgChatMessageVariant.SUPPLIER_SOURCING]: IntakeSupplierSourcingStep,
  [SvbrgChatMessageVariant.EXTENDED_LINE_ITEMS_CHECK]: IntakeExtendedLineItemsCheckStep,
  [SvbrgChatMessageVariant.REQUEST_NUCLEAR_COMPLIANCE_FORM]: IntakeNuclearComplianceFormStep,
};

const CHAT_SESSION_COMPLETED: {
  [key in SvbrgIntakeStatus]: boolean;
} = {
  [SvbrgIntakeStatus.DRAFT]: false,
  [SvbrgIntakeStatus.BLOCKED]: false,
  [SvbrgIntakeStatus.COMPLETED]: true,
  [SvbrgIntakeStatus.CANCELLED]: true,
  [SvbrgIntakeStatus.IN_PROGRESS]: true,
  [SvbrgIntakeStatus.RELEASED]: true,
};

export const IntakeDetail = () => {
  const { chatSession, chatMessages } = useIntakeDetail();

  const renderedChatMessageSteps = chatMessages
    .map(({ id, variant, status, message, role }) => {
      if (chatMessageStepMap[variant]) {
        const ChatMessageStepVariant = chatMessageStepMap[variant] as FunctionComponent<ChatMessageProps>;

        return (
          <ChatMessageStepVariant
            chatMessageId={id}
            key={id}
            status={status}
            isCompleted={status === SvbrgChatMessageStatus.COMPLETED}
            chatSessionStatus={chatSession?.status}
            chatSessionCompleted={CHAT_SESSION_COMPLETED[chatSession?.status ?? SvbrgIntakeStatus.DRAFT]}
            message={message}
            role={role}
            createdBy={chatSession?.createdBy}
          />
        );
      }
      return null;
    })
    .filter((component) => component !== null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Ensure all components are rendered before scrolling to bottom position
    window.scrollTo({ top: messagesEndRef.current?.offsetTop, behavior: 'smooth' });
  }, [chatMessages]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          padding: 4,
          alignItems: 'center',
          width: '100%',
          overflowY: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            width: '100%',
            maxWidth: 'xl',
            gap: 4,
          }}
        >
          {renderedChatMessageSteps}
        </Box>
      </Box>
      <div ref={messagesEndRef} />
    </>
  );
};
